import {
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import simccLogo from "../assets/images/simcc-logo-128.png";
import {
  isAdmin,
  isAdminOrPartnerOrAssistant,
  isAdminOrPartnerOrAssistantOrManager,
} from "../functions/checkrole";
import {
  abortSilently,
  refreshIjhsPendingCount,
} from "../functions/getData";
import { Drawer, DrawerHeader } from "./DrawerAndAppBar";
import NavIcon from "./NavIcons";
import "./NavigationShell.css";

const formatNavBadgeCount = (count) => (count > 99 ? "99+" : String(count));

const getNavigationSections = () => [
  {
    id: "dashboard",
    label: "Overview",
    solo: true,
    items: [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: <NavIcon name="dash" />,
      },
    ],
  },
  {
    id: "competitions",
    label: "Competitions",
    items: [
      isAdminOrPartnerOrAssistant() && {
        label: "All Competitions",
        path: "/competitions",
        icon: <NavIcon name="trophy" />,
      },
      isAdmin() && {
        label: "Team Competitions",
        path: "/teamcompetitions",
        icon: <NavIcon name="team" />,
      },
    ],
  },
  {
    id: "content-bank",
    label: "Content Bank",
    items: [
      isAdmin() && {
        label: "Tasks",
        path: "/task",
        icon: <NavIcon name="file" />,
      },
      isAdmin() && {
        label: "Collections",
        path: "/collection",
        icon: <NavIcon name="layers" />,
      },
      //TEMP DISABLED
      // {
      //   label: "Translations",
      //   icon: <NavIcon name="lang" />,
      //   children: [
      //     { label: "Assign Translation", path: "/assigntranslation" },
      //     { label: "Translate", path: "/translate" },
      //     {
      //       label: "Download Contest Paper",
      //       path: "/downloadcontestpaper",
      //     },
      //   ],
      // },
      isAdmin() && {
        label: "Domains & Tags",
        path: "/domain",
        icon: <NavIcon name="tag" />,
      },
      isAdmin() && {
        label: "Difficulty Groups",
        path: "/difficultygroup",
        icon: <NavIcon name="tune" />,
      },
    ],
  },
  {
    id: "participants",
    label: "Participants",
    items: [
      {
        label: "Participants",
        path: "/participants",
        icon: <NavIcon name="people" />,
      },
      {
        label: "Imports",
        path: "/uploadedparticipantsfiles",
        icon: <NavIcon name="upload" />,
      },
      {
        label: "IJHS Auto Detection",
        path: "/ijhsautodetection",
        icon: <NavIcon name="tag" />,
        badgeKey: "ijhs",
      },
    ],
  },
  {
    id: "certificates",
    label: "Certificates",
    items: [
      isAdmin() && {
        label: "Templates",
        path: "/certificatetemplates",
        icon: <NavIcon name="cert" />,
      },
      {
        label: "Print Certificates",
        path: "/printcertificate",
        icon: <NavIcon name="print" />,
      },
    ],
  },
  {
    id: "administration",
    label: "Administration",
    items: [
      isAdminOrPartnerOrAssistantOrManager() && {
        label: "Users",
        path: "/users",
        icon: <NavIcon name="user" />,
      },
      isAdmin() && {
        label: "Organizations",
        path: "/organizations",
        icon: <NavIcon name="org" />,
      },
      {
        label: "Schools",
        path: "/schools",
        icon: <NavIcon name="school" />,
      },
    ],
  },
];

const RAIL_HOVER_DELAY = 150;

const isItemActive = (item, pathname) =>
  item.path === pathname ||
  Boolean(item.children?.some((child) => child.path === pathname));

export default function SideBar({ open, toggleDrawer }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const canHover = useMediaQuery("(hover: hover)");
  const sections = getNavigationSections()
    .map((section) => ({
      ...section,
      items: section.items.filter(Boolean),
    }))
    .filter((section) => section.items.length);
  const activeSectionId = sections.find((section) =>
    section.items.some((item) => isItemActive(item, location.pathname))
  )?.id;
  // On desktop the collapsed rail expands while the pointer is over its items;
  // `open` is the pinned state toggled from the top bar or the header chevron.
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const expanded = open || (hoverExpanded && !isMobile);
  const hoverTimer = useRef();
  const paperRef = useRef(null);
  const ijhsPendingCount = useSelector((state) => state.ijhsPendingCount);
  // Sections start expanded each time the sidebar opens.
  const [collapsedSections, setCollapsedSections] = useState({});
  const [translationsOpen, setTranslationsOpen] = useState(
    ["/assigntranslation", "/translate", "/downloadcontestpaper"].includes(
      location.pathname
    )
  );

  useEffect(() => {
    const controller = new AbortController();
    refreshIjhsPendingCount(controller.signal);
    return () => abortSilently(controller);
  }, []);

  useEffect(() => {
    if (
      ["/assigntranslation", "/translate", "/downloadcontestpaper"].includes(
        location.pathname
      )
    ) {
      setTranslationsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  useEffect(() => {
    if (!expanded) setCollapsedSections({});
  }, [expanded]);

  // The expanded sidebar overlays the page on desktop, so close it when the
  // user clicks anywhere outside of it.
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const handlePointerDown = (event) => {
      if (!paperRef.current?.contains(event.target)) toggleDrawer(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open, isMobile, toggleDrawer]);

  // Close the overlay after navigating; on desktop it stays expanded until the
  // pointer leaves it.
  const closeAfterNavigate = () => {
    if (!open) return;
    setHoverExpanded(canHover && !isMobile);
    toggleDrawer(false);
  };

  const isSectionOpen = (sectionId) => !collapsedSections[sectionId];

  const toggleSection = (sectionId) => {
    setCollapsedSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  const handleRailEnter = () => {
    if (expanded || isMobile || !canHover) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(
      () => setHoverExpanded(true),
      RAIL_HOVER_DELAY
    );
  };

  const handleSidebarLeave = () => {
    clearTimeout(hoverTimer.current);
    setHoverExpanded(false);
  };

  const collapseSidebar = () => {
    clearTimeout(hoverTimer.current);
    setHoverExpanded(false);
    toggleDrawer(false);
  };

  const handleNestedGroupClick = () => {
    if (!expanded) {
      toggleDrawer(true);
      setTranslationsOpen(true);
      return;
    }
    setTranslationsOpen((current) => !current);
  };

  return (
    <Drawer
      className={`simcc-sidebar ${expanded ? "is-expanded" : "is-rail"}`}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      expanded={expanded}
      mobile={isMobile}
      onClose={() => toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ ref: paperRef, onMouseLeave: handleSidebarLeave }}
    >
      <DrawerHeader className="simcc-sidebar__header">
        <Link
          to="/dashboard"
          className="simcc-sidebar__brand"
          onClick={closeAfterNavigate}
          aria-label="SIMCC dashboard"
        >
          <img src={simccLogo} alt="" className="simcc-sidebar__logo" />
          {expanded && (
            <span className="simcc-sidebar__brand-copy">
              <strong>SIMCC</strong>
              <small>Management Portal</small>
            </span>
          )}
        </Link>
        <IconButton
          className="simcc-sidebar__collapse"
          onClick={expanded ? collapseSidebar : () => toggleDrawer(true)}
          aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
          aria-expanded={expanded}
          size="small"
        >
          <NavIcon name="panel" size={16} strokeWidth={1.9} />
        </IconButton>
      </DrawerHeader>

      <nav
        className="simcc-sidebar__nav"
        aria-label="Primary navigation"
        onMouseEnter={handleRailEnter}
        onMouseLeave={() => clearTimeout(hoverTimer.current)}
      >
        {sections.map((section) => (
          <section className="simcc-sidebar__section" key={section.label}>
            {/* The title stays rendered on the rail (drawn as a divider) so
                the icons keep the same vertical position when expanding. */}
            {!section.solo && (
              <button
                type="button"
                className={`simcc-sidebar__section-title${
                  section.id === activeSectionId ? " is-active" : ""
                }${isSectionOpen(section.id) ? "" : " is-closed"}`}
                onClick={() => toggleSection(section.id)}
                aria-expanded={isSectionOpen(section.id)}
                aria-controls={`navigation-section-${section.id}`}
                aria-hidden={!expanded || undefined}
                tabIndex={expanded ? undefined : -1}
              >
                <span>{section.label}</span>
                <NavIcon
                  name="chevronDown"
                  size={12}
                  strokeWidth={2.5}
                  className="simcc-sidebar__section-chevron"
                />
              </button>
            )}
            <Collapse
              in={section.solo || !expanded || isSectionOpen(section.id)}
              timeout="auto"
            >
              <List
                disablePadding
                id={`navigation-section-${section.id}`}
                className="simcc-sidebar__section-items"
              >
                {section.items.map((item) => {
                  const active = isItemActive(item, location.pathname);
                  const badgeCount =
                    item.badgeKey === "ijhs" ? ijhsPendingCount : 0;
                  const badgeLabel =
                    badgeCount > 0
                      ? `${item.label}, ${badgeCount} groups to review`
                      : item.label;
                  const button = (
                    <ListItemButton
                      component={item.path ? Link : "button"}
                      to={item.path}
                      onClick={
                        item.children
                          ? handleNestedGroupClick
                          : closeAfterNavigate
                      }
                      selected={active}
                      className="simcc-sidebar__item"
                      aria-label={badgeLabel}
                      aria-expanded={
                        item.children ? expanded && translationsOpen : undefined
                      }
                    >
                      <ListItemIcon className="simcc-sidebar__item-icon">
                        {!expanded && badgeCount > 0 ? (
                          <span className="simcc-sidebar__icon-wrap">
                            {item.icon}
                            <span className="simcc-sidebar__rail-badge">
                              {formatNavBadgeCount(badgeCount)}
                            </span>
                          </span>
                        ) : (
                          item.icon
                        )}
                      </ListItemIcon>
                      {expanded && (
                        <ListItemText
                          className="simcc-sidebar__item-text"
                          primary={item.label}
                        />
                      )}
                      {expanded && badgeCount > 0 && (
                        <span className="simcc-sidebar__count-badge">
                          {formatNavBadgeCount(badgeCount)}
                        </span>
                      )}
                      {item.children && expanded && (
                        <NavIcon
                          name="chevronRight"
                          size={12}
                          strokeWidth={2.5}
                          className={`simcc-sidebar__chevron${
                            translationsOpen ? " is-open" : ""
                          }`}
                        />
                      )}
                    </ListItemButton>
                  );

                  return (
                    <div key={item.label}>
                      <Tooltip
                        title={badgeLabel}
                        placement="right"
                        disableHoverListener={expanded || canHover}
                        arrow
                      >
                        {button}
                      </Tooltip>
                      {item.children && (
                        <Collapse
                          in={expanded && translationsOpen}
                          timeout="auto"
                        >
                          <List
                            disablePadding
                            className="simcc-sidebar__subnav"
                          >
                            {item.children.map((child) => (
                              <ListItemButton
                                component={Link}
                                to={child.path}
                                onClick={closeAfterNavigate}
                                selected={location.pathname === child.path}
                                className="simcc-sidebar__subitem"
                                key={child.path}
                              >
                                <ListItemText primary={child.label} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      )}
                    </div>
                  );
                })}
              </List>
            </Collapse>
          </section>
        ))}
      </nav>
    </Drawer>
  );
}

SideBar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};
